import React from "react";
import { GridCellTooltip } from "../GridCellTooltip/GridCellTooltip";
import tableStyles from "../../PreviewImportModalTable.module.css";
import { getMatrixRow, stringifyValue } from "../../PreviewImportModal.utils";
import styles from "./ActivityMatrixTable.module.css";
import type { ActivityMatrixTableProps } from "./ActivityMatrixTable.types";
import {
  getActivityHeaderColSpan,
  getActivityRowKey,
  getActivityTableMinWidth,
  getActivityValueAriaLabel,
} from "./ActivityMatrixTable.utils";

export const ActivityMatrixTable: React.FC<ActivityMatrixTableProps> = ({
  activities,
  rowIndexes,
  onValueChange,
}) => {
  const tableMinWidth = getActivityTableMinWidth(activities.length);

  return (
    <table className={`${styles.activityTable} ${tableStyles.table}`} style={{ minWidth: tableMinWidth }}>
      <colgroup>
        <col className={tableStyles.descriptionCell} />
        {activities.map((activity) => (
          <col key={`origin-col-${activity.key}`} className={tableStyles.activityValueColumn} />
        ))}
        <col className={tableStyles.arrowCell} />
        <col className={tableStyles.descriptionCell} />
        {activities.map((activity) => (
          <col key={`destination-col-${activity.key}`} className={tableStyles.activityValueColumn} />
        ))}
      </colgroup>
      <thead>
        <tr className={tableStyles.groupHeader}>
          <th colSpan={getActivityHeaderColSpan(activities.length)}>
            <div className={tableStyles.groupHeaderLabels}>
              <span>Origin</span>
              <span>Destination</span>
            </div>
          </th>
        </tr>
        <tr className={tableStyles.columnHeader}>
          <th className={tableStyles.descriptionCell}>Description</th>
          {activities.map((activity) => (
            <th key={`origin-${activity.key}`} className={tableStyles.activityValueColumn} title={activity.label}>
              Value
            </th>
          ))}
          <th className={tableStyles.arrowCell} aria-hidden="true">
            <span className={tableStyles.arrowText}>&rarr;</span>
          </th>
          <th className={tableStyles.descriptionCell}>Description</th>
          {activities.map((activity) => (
            <th key={`destination-${activity.key}`} className={tableStyles.activityValueColumn} title={activity.label}>
              Value
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rowIndexes.map((rowIndex) => {
          const fallbackRow = getMatrixRow(activities, rowIndex);
          return (
            <tr key={getActivityRowKey(rowIndex)}>
              <td className={tableStyles.descriptionCell}>
                <GridCellTooltip text={fallbackRow?.ParentFieldDescription} />
              </td>
              {activities.map((activity) => {
                const parentValue = stringifyValue(activity.fields[rowIndex]?.ParentValue);
                return (
                  <td key={`parent-${activity.key}-${rowIndex}`} className={tableStyles.activityValueColumn}>
                    <GridCellTooltip text={parentValue} />
                  </td>
                );
              })}
              <td className={tableStyles.arrowCell} aria-hidden="true">
                <span className={tableStyles.arrowText}>&rarr;</span>
              </td>
              <td className={tableStyles.descriptionCell}>
                <GridCellTooltip text={fallbackRow?.ChildFieldDescription} />
              </td>
              {activities.map((activity) => {
                const row = activity.fields[rowIndex];
                const childValue = stringifyValue(row?.ChildValue);
                return (
                  <td key={`child-${activity.key}-${rowIndex}`} className={tableStyles.activityValueColumn}>
                    {row ? (
                      <input
                        className={tableStyles.valueInput}
                        value={childValue}
                        onChange={(event) => onValueChange(activity.key, rowIndex, event.target.value)}
                        aria-label={getActivityValueAriaLabel(activity.label)}
                      />
                    ) : null}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
